import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';

const variables = {
  borderColor: '#BCC2D0',
  placeholderColor: '#7985A0',
  contentColor: '#252A34',
  boldFontWeight: 600,
  primaryColor: '#0076DD',
  spinnerColor: '#dfe4ee',
  backgroundWhite: '#FFFFFF',
};

const commonSelectStyles = `
  .p-select-header .p-inputtext,
  .p-multiselect-filter-container .p-inputtext {
    padding-inline: 32px;
  }

  .p-select-dropdown, .p-multiselect-dropdown  {
    width: 10px;
    margin-right: 12px;
    margin-left: 8px;
  }

  .p-multiselect {
   &:hover {
    .p-multiselect-clear-icon {
       display: flex;
    }
   }
  }

  .p-multiselect-clear-icon {
   display: none;
  }

  .p-select-clear-icon, .p-multiselect-clear-icon {
    .p-icon {
      width: 12px;
      height: 12px;
    }
  }

  .p-multiselect-list-container, .p-select-list-container, .p-autocomplete-list-container {
    font-size: 14px;
    &::-webkit-scrollbar {
       width: 6px;
       height: 6px;
       background: #fff;
       z-index: 999999;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      background: #9aa6bc;
    }
  }

  .p-multiselect-filter-container, .p-select-header {
    .p-inputicon {
      left: 12px;
      width: 20px;
      height: 20px;
    }
  }

  .p-select, .p-multiselect {
    .p-overlay {
      width: 100%;
    }
  }

  .form-control-container {
    .p-select, .p-multiselect {
      .p-overlay {
        top: 40px!important;
      }
    }
  }

  .p-select-clear-icon {
    width: 12px;
    visibility: hidden;
    &: hover {
      visibility: visible;
    }
  }

  .p-select-label:hover + .p-select-clear-icon {
    visibility: visible;
  }
`;
export const primengCustomPreset = definePreset(Lara, {
  semantic: {
    colorScheme: {
      light: {
        root: {
          primaryColor: variables.primaryColor,
          highlightBackground: '#E0F1FF',
          highlightColor: variables.contentColor,
        },
        formField: {
          borderColor: variables.borderColor,
          hoverBorderColor: '#5D6983',
          focusBorderColor: variables.primaryColor,
          placeholderColor: variables.placeholderColor,
          focusRingShadow: 'none',
          color: variables.contentColor,
          disabledBackground: '#F9FAFB',
          backgroundColor: variables.backgroundWhite,
          invalidBorderColor: '#D6421E',
          invalidPlaceholderColor: variables.placeholderColor,
          paddingX: '12px',
          paddingY: '8px',
          css: () => `
            .form-control-field, .p-autocomplete-input {
              &:focus-visible {
                box-shadow: 0px 0px 0px 4px rgba(0, 118, 221, 0.16)!important;
              }
            }
            .form-control-field:focus-visible, .p-autocomplete-input:focus-visible, {
              box-shadow: 0px 0px 0px 4px rgba(0, 118, 221, 0.16)!important;
            }
            .input:focus-visible {
              box-shadow: 0px 0px 0px 4px rgba(0, 118, 221, 0.16)!important;
            }
          `,
        },
        text: {
          color: variables.contentColor,
        },
      },
    },
  },
  components: {
    button: {
      icon: {
        only: {
          width: '24px',
        },
      },
      text: {
        primary: {
          color: '#5D6983!important',
          hover: {
            background: 'none!important',
          },
          active: {
            background: 'none!important',
          },
        },
      },
    },
    checkbox: {
      disabled: {
        background: 'rgb(222, 225, 231)',
      },
      checked: {
        disabled: {
          border: {
            color: 'rgb(222, 225, 231)',
          },
        },
      },
      icon: {
        disabled: {
          color: '#ffffff',
        },
      },
      css: () => `
          .p-checkbox-box, .p-checkbox {
            width: 16px;
            height: 16px;
          }
          .p-checkbox.p-disabled .p-checkbox-box {
            opacity: 0.5;
          }
          .p-checkbox-box {
            .p-iconwrapper {
              width: 12px;
              height: 12px;
            }
          }
          .p-checkbox-checked:not(.p-disabled):has(.p-checkbox-input:hover) .p-checkbox-box {
            background: #0076DD;
            border: #0076DD;
          }
      `,
    },
    radiobutton: {
      width: '16px',
      height: '16px',
      checked: {
        border: {
          color: '#0076DD',
        },
        background: '#FFFFFF',
        hover: {
          border: {
            color: '#0068C2',
          },
          background: 'rgb(235, 244, 253)',
        },
      },
      icon: {
        size: '8px',
        checked: {
          color: '#0076DD',
          hover: {
            color: '#0068C2',
          },
        },
      },
    },
    inputtext: {
      disabled: {
        background: variables.backgroundWhite,
      },
      css: () => `
        .p-inputtext {
          font-size: 14px!important;
          line-height: 18px;
          font-variant-numeric: lining-nums tabular-nums;
          font-weight: 400;
          cursor: pointer;
        }
        `,
    },
    multiselect: {
      list: {
        padding: '8px 4px',
        header: {
          padding: '8px 8px 0px 14px',
        },
        gap: '1px',
      },
      option: {
        padding: '10px',
        focus: {
          background: '#5D698314',
        },
        selected: {
          background: 'none',
          focus: {
            background: '#5D698314',
            color: '#252A34',
          },
        },
        border: {
          radius: '4px',
        },
      },
      css: () => `
        ${commonSelectStyles}
        .p-multiselect-overlay {
          font-family: 'Inter';
          .p-checkbox-box, .p-checkbox {
            width: 16px;
            height: 16px;
          }
          .p-checkbox-box {
            .p-iconwrapper {
              width: 12px;
              height: 12px;
            }
          }
          .p-checkbox-checked:not(.p-disabled):has(.p-checkbox-input:hover) .p-checkbox-box {
            background: #0076DD;
            border: #0076DD;
          }
        }

        .p-iconwrapper {
          color: #516285;
        }

        .p-multiselect-option:not(.p-multiselect-option-selected):not(.p-disabled).p-focus  {
          border-radius: 4px;
        }

        .p-multiselect-dropdown-icon {
          width: 16px;
          height: 16px;
        }

        .p-multiselect-filter-container {
           .p-iconfield {
              display: inline-flex!important;
              align-items: center;
           }
        }

        .p-multiselect-filter-container, .p-select-header {
          .p-inputicon {
              width: 16px;
              height: 16px;
              top: 50%;
              margin: 0;
              transform: translate(-4px, -50%);
          }
        }

        .p-multiselect-filter-icon {
          width: 16px;
          height: 16px;
        }
      `,
    },
    select: {
      option: {
        selected: {
          focus: {
            background: 'rgba(0, 118, 221, 0.12)',
            color: variables.contentColor,
          },
        },
        border: {
          radius: '6px',
        },
        focus: {
          background: 'rgba(93, 105, 131, 0.08)',
        },
      },
      list: {
        padding: '8px 4px',
        gap: '1px',
      },
      css: () => `
      ${commonSelectStyles}
        .p-select-label {
          text-align: left;
        }

        .p-iconwrapper {
          color: #516285;
        }

        .p-select-clear-icon {
          position: unset;
          margin: 0;
        }

        input.p-select-label {
          cursor: pointer;
        }
      `,
    },
    datepicker: {
      datePadding: '0px',
      header: {
        padding: 0,
      },
      panel: {
        padding: '16px',
      },
      date: {
        hover: {
          background: 'rgba(93, 105, 131, 0.08)',
          color: '#4A5469',
        },
      },
      week: {
        day: {
          color: variables.placeholderColor,
          fontWeight: variables.boldFontWeight,
          padding: '4px 4px 8px 4px',
          view: {
            margin: '16px',
          },
        },
      },
      month: {
        view: {
          margin: '40px 0 0 0',
        },
      },
      time: {
        picker: {
          gap: '20px',
        },
      },
      title: {
        font: {
          weight: variables.boldFontWeight,
        },
      },
      css: () => `
        .p-datepicker {
          width: 100%;
        }

        .p-datepicker .p-datepicker-panel {
          min-width: unset;
        }

        .popover-right-content {
          .p-datepicker {
            min-height: 296px;
            min-width: 270px;
          }
        }

        .datepicker-wrapper, .p-datepicker {
         .p-inputtext {
            padding-inline: 32px;
            &:placeholder-shown {
              padding-inline: unset;
              padding-inline-start: 32px;
              padding-inline-end: 12px;
            }
          }
        }

        .p-datepicker-input-icon-container {
          left: 12px;
          margin: 0;
          transform: translateY(-50%);
          .p-icon {
            width: 16px!important;
            height: 16px!important;
          }
        }

        .p-datepicker-panel {
          border: none;
          border-bottom-right-radius: 6px;
          border-top-right-radius: 6px;
          width: 270px;
        }

        .p-datepicker-header {
          border: none;
        }

        .p-datepicker-day-cell {
          .p-datepicker-day {
            width: 32px;
            height: 32px;
            margin-top: 4px;
            border-radius: 6px;
            margin-right: 2px;
          }
        }

        .p-datepicker-today > .p-datepicker-day:not(.p-datepicker-day-selected) {
          border-color: #0076DD;
          background: none;
        }

        .p-datepicker-day-selected-range {
          background: #E0F1FF!important;
          border: none;
        }

        .p-datepicker-prev-button, .p-datepicker-next-button{
          color: #5D6983!important;
        }

        .p-datepicker-prev-button:hover, .p-datepicker-next-button:hover {
          background: none!important;
        }

        .p-datepicker-day-cell .p-datepicker-day, .p-datepicker-day-view {
          font-size: 14px;
        }

        .p-datepicker-month-view {
          display: flex;
          justify-content: center;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 16px;
        }

        .p-datepicker-month {
          width: 60px;
          height: 28px;
          font-size: 14px;
        }

        .p-datepicker-year {
          height: 28px;
          width: calc(50% - 32px);
          font-size: 14px;
        }

        .p-datepicker-year-view {
          gap: 16px;
          display: flex;
          justify-content: center;
          flex-direction: row;
          flex-wrap: wrap;
        }

        .p-datepicker-select-year, .p-datepicker-select-month {
          font-size: 16px;
          font-family: Inter, Arial, serif;
        }

        .p-datepicker-time-picker {
          margin-top: 16px;
        }

        .p-datepicker-time-picker span {
          font-size: 14px;
        }

        .p-datepicker-input-icon-container {
          width: 16px;
          height: 16px;
        }
    `,
    },
    dialog: {
      footer: {
        padding: '8px 24px 24px 24px',
      },
      title: {
        font: {
          weight: 600,
          size: '18px',
        },
      },
      css: () => `
        .p-dialog-header {
          padding: 24px;
        }

        .p-dialog-header-actions {
          .p-iconwrapper {
            svg {
              width:14px !important;
              height: 14px !important;
            }
          }
        }

        .p-dialog-title {
          line-height: 22px;
        }

        .p-dialog-content {
          padding: 0 24px 19px 24px;
          margin-bottom: 5px;
          &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
            background: #fff;
            z-index: 999999;
          }
          &::-webkit-scrollbar-thumb {
            border-radius: 5px;
            background: #9aa6bc;
          }
        }

        .dialog-content-overflow-visible.p-dialog-content {
          overflow: visible !important;
        }

        .p-dialog-close-button {
          border-radius: 4px!important;
          &:hover {
            border-radius: 4px;
            background: rgba(93, 105, 131, 0.08);
          }

          &:active {
            border-radius: 4px;
            background: rgba(93, 105, 131, 0.22);
          }
        }
      `,
    },
    panel: {
      toggleable: {
        header: {
          padding: '0',
        },
      },
      background: '#FFF',
      border: {
        radius: '8px',
      },
      header: {
        background: '#FFF!important',
        color: variables.placeholderColor,
        padding: 0,
      },
      content: {
        padding: '16px 0 0 0',
      },
      css: () => `
        .p-panel {
          box-shadow: 0px 1px 4px 0px rgba(19, 21, 26, 0.06), 0px 1px 2px 0px rgba(19, 21, 26, 0.02);
          border: none;
          padding: 20px;
        }

        .p-overlay {
          top: 65px;
        }

        .p-panel-header {
          border: none;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: 20px;
        }

        .p-panel-toggler {
          display: none!important;
        }

        .p-panel-icons {
          height: 36px;
        }

        .panel-icons {
          width: 36px;
          height: 36px;
          padding: 8px;
          cursor: pointer;
          &:active {
            background: rgba(93, 105, 131, 0.22);
            border-radius: 4px;
          }
          &:hover {
            border-radius: 4px;
            background: rgba(93, 105, 131, 0.08);
          }
         }

         .test-btn {
                   width: 36px;
          height: 36px;
          padding: 8px;
          cursor: pointer;
          &:active {
            background: rgba(93, 105, 131, 0.22);
            border-radius: 4px;
          }
          &:hover {
            border-radius: 4px;
            background: rgba(93, 105, 131, 0.08);
          }
          }

        .filters-content {
          .filter-options {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            // grid-template-columns: repeat(4, 1fr);
            gap: 12px 16px;
          }
          .filter-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 32px;
            gap: 16px;
          }
        }
      `,
    },
    popover: {
      content: {
        padding: '0px',
      },
      border: {
        radius: '6px',
      },
      gutter: '4px',
      shadow:
        '0px 8px 16px -4px rgba(19, 21, 26, 0.10), 0px 2px 4px -2px rgba(19, 21, 26, 0.04)',
      css: () => `
        .p-popover:before, .p-popover:after {
          display: none;
        }
      `,
    },
    progressspinner: {
      color: {
        1: variables.spinnerColor,
        2: variables.spinnerColor,
        3: variables.spinnerColor,
        4: variables.spinnerColor,
      },
      css: () => `
      .p-progressspinner-circle {
        stroke: #dfe4ee!important
      }

      .outlined-color {
        .p-progressspinner-circle {
          stroke: #0076dd!important
        }
      }
      `,
    },
    tooltip: {
      padding: '8px',
      background: '#4A5469',
      borderRadius: '8px',
      maxWidth: '320px',
      css: () => `
        .p-tooltip-arrow {
          border-top-color: #4A5469!important;
        }
        .p-tooltip-text {
          color: #FFF;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 16px;
          background: #4A5469;
        }
      `,
    },
    treetable: {
      header: {
        cell: {
          border: {
            color: '#F9FAFB',
          },
          hover: {
            background: 'rgba(93, 105, 131, 0.08)',
          },
          padding: '4px 12px!important',
        },
      },
      body: {
        cell: {
          padding: '10.7px 12px!important',
        },
      },
      row: {
        hover: {
          background: 'rgba(93, 105, 131, 0.08)',
        },
        selected: {
          background: '#0076DD1F',
        },
      },
      column: {
        title: {
          font: {
            weight: 600,
          },
        },
      },
      css: () => `
      .p-treetable .p-treetable-thead > tr > th,
      .p-treetable .p-treetable-tbody > tr > td {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        height: 40px;
        line-height: 18px;
      }

      .p-treetable .p-treetable-thead > tr > th {
        height: 32px;
        font-size: 12px;
        &:hover {
          background: rgba(93, 105, 131, 0.08) !important;
        }
      }
        .text-align-right {
          text-align: right!important;
        }

        .text-align-left {
          text-align: left!important;
        }
      .p-treetable .p-treetable-tbody > tr > td {
        font-size: 14px;
        font-weight: 400;
      }

      .p-treetable-tbody > tr > td {
        .text-align-right {
          text-align: right!important;
        }

        .text-align-left {
          text-align: left!important;
        }
      }

      .p-treetable-thead {
        border-top: 6px;
      }

      .p-treetable-scrollable-body {
        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
          background: #fff;
          z-index: 999999;
        }
        &::-webkit-scrollbar-thumb {
          border-radius: 5px;
          background: #9aa6bc;
        }
      }

      .p-paginator-pages, .p-paginator {
        gap: 0!important;
      }

      .p-paginator-page, .p-paginator-next, .p-paginator-last, .p-paginator-first, .p-paginator-prev {
        border-radius: 0!important;
        border: 1px solid #DEE1E7!important;
        border-width: 1px 0 1px 1px!important;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 16px;
        color: #4A5469;
        padding: 6px 10px;
      }

      .p-paginator {
        border-radius: 0!important;
      }

      .p-paginator-last {
        border-top-right-radius: 6px!important;
        border-bottom-right-radius: 6px!important;
        border-right: 1px solid #DEE1E7!important;
        margin-right: 8px!important;
      }

      .p-paginator-first {
        border-top-left-radius: 6px!important;
        border-bottom-left-radius: 6px!important;
      }

      .tree-table-wrapper {
        .p-select, .p-multiselect {
          .p-overlay {
            margin-top: -4px!important;
          }
        }
        .p-select-list-container {
          max-height: 205px!important;
        }
      }

      .p-treetable-scrollable-wrapper {
        min-height: 375px;
      }

      .p-paginator {
        .p-select-label {
           font-size: 12px;
           font-weight: 500;
           line-height: 18px;
           color: #4A5469;
           padding: 0;
        }
      }

      .p-paginator-rpp-dropdown {
        padding: 7px 12px;
        width: 80px;
        height: 32px;
        .p-select-dropdown, .p-multiselect-dropdown {
          width: 16px;
          height: 16px;
          margin: 0;
          svg {
            width: 14px;
            height: 14px;
          }
        }
      }
      `,
    },
    paginator: {
      nav: {
        button: {
          width: '36px',
          height: '32px',
          selected: {
            color: '#4A5469',
          },
          color: '#4A5469',
        },
      },
      padding: '16px 0 0 0',
    },
    toast: {
      details: {
        font: {
          size: '14px!important',
        },
      },
      text: {
        gap: '0px!important',
      },
      content: {
        gap: '16px!important',
        padding: '12px!important',
      },
      border: {
        width: '1px',
        radius: '8px',
      },
      error: {
        color: '#802812!important',
        close: {
          button: {
            hover: {
              background: 'rgba(93, 105, 131, 0.08)',
            },
          },
          icon: {
            size: '14px!important',
          },
        },
        border: {
          color: '#F2B1A2!important',
        },
        background: '#FEF8F6!important',
        shadow:
          '0px 16px 28px -4px rgba(19, 21, 26, 0.10), 0px 8px 10px -4px rgba(19, 21, 26, 0.04)!important',
      },
      success: {
        color: '#00442D!important',
        border: {
          color: '#69FFCB!important',
        },
        background: '#F0FFFA!important',
        shadow:
          '0px 16px 28px -4px rgba(19, 21, 26, 0.10), 0px 8px 10px -4px rgba(19, 21, 26, 0.04)!important',
      },
      info: {
        border: {
          color: '#8BC9FF!important',
        },
        background: '#F5FAFF!important',
        color: '#004785',
        shadow:
          '0px 16px 28px -4px rgba(19, 21, 26, 0.10), 0px 8px 10px -4px rgba(19, 21, 26, 0.04)!important',
      },
      warn: {
        border: {
          color: '#FAD29D!important',
        },
        background: '#FEF8F1!important',
        color: '#905508!important',
        shadow:
          '0px 16px 28px -4px rgba(19, 21, 26, 0.10), 0px 8px 10px -4px rgba(19, 21, 26, 0.04)!important',
      },
      css: () => `
          .p-toast-message-icon {
             .p-icon {
               width: 16px !important;
               height: 16px !important;
             }
          }

         .p-toast {
            z-index: 99999999!important;
            width: max-content;
         }

         .p-toast-summary {
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 20px;
        }
        .p-toast-close-button {
          color: #5D6983;
          margin: 0;
          border-radius: 4px!important;
          .p-icon {
            width: 14px;
            height: 14px;
          }

          &:hover {
            background: rgba(93, 105, 131, 0.08)!important;
          }

          &:active {
            background: rgba(93, 105, 131, 0.22)!important;
          }
        }

        .p-toast-close-icon {
          width: 14px!important;
          height: 14px!important;
        }

        .p-toast-message-success {
          color: #00442D;
          .p-toast-message-icon {
            color: #008859;
          }
        }
        .p-toast-message-info {
         color: #004785;
        }
        .p-toast-message-error {
          .p-toast-summary {
            color: #802812;
          }
          .p-toast-detail {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 18px;
            color: #802812;
          }
          .p-toast-message-error .p-toast-close-button:hover {
             border-radius: 4px;
          }
        }

        div:has(> .p-toast-close-button) {
          margin-left: auto!important;
        }
      `,
    },
  },
});
